/**
 * Scale denominators, and the two conventions that disagree about them.
 *
 * WMS capabilities advertise when a layer is allowed to draw as a pair of scale
 * denominators. Ignoring them is not a cosmetic omission: 227 of the 308 layers
 * this application publishes carry one, and a layer asked for outside its window
 * comes back as a valid, fully transparent PNG. The map looks broken and nothing
 * anywhere reports an error.
 */

/**
 * The pixel size OGC 06-042 assumes when it converts a resolution into a scale
 * denominator: 0.28 mm, the size of a pixel on a "standardized rendering
 * device". GeoServer computes the scale it compares against Min/Max with this.
 */
export const OGC_PIXEL_M = 0.00028;

/**
 * The pixel size the ArcGIS SDK assumes for `view.scale`: 1/96 inch, the CSS
 * reference pixel.
 */
export const SCREEN_PIXEL_M = 0.0254 / 96;

/**
 * Ratio between the two. Only 5.8%, which is exactly why it is dangerous: it is
 * far too small to look wrong and just large enough to put a layer on the other
 * side of a zoom boundary from where it belongs.
 */
export const OGC_TO_SCREEN = OGC_PIXEL_M / SCREEN_PIXEL_M;

/** OGC scale denominator of a 256px Web Mercator tile grid at zoom 0. */
export const OGC_SCALE_AT_Z0 = 559_082_264.028;

/**
 * A scale window, as the capabilities document states it.
 *
 * `min` is `MinScaleDenominator` and `max` is `MaxScaleDenominator`. A smaller
 * denominator means more zoomed in, so `min` is the zoomed-in limit and `max`
 * the zoomed-out one. Either may be absent, meaning unrestricted in that
 * direction.
 */
export interface ScaleWindow {
  readonly minScale?: number;
  readonly maxScale?: number;
}

/**
 * Convert a WMS scale window into the ArcGIS SDK's `minScale` / `maxScale`.
 *
 * **The two names are swapped.** Esri's `minScale` is the minimum *zoom level*
 * at which a layer shows - that is, the most zoomed-out limit, the larger
 * denominator - which is WMS's `MaxScaleDenominator`. Getting this backwards
 * hides every layer at exactly the zooms where it should appear, and the
 * symptom is indistinguishable from the bug this function exists to fix.
 */
export function toEsriScaleRange(window: ScaleWindow): {
  minScale: number;
  maxScale: number;
} {
  return {
    // Esri: most zoomed out. 0 means no limit.
    minScale: window.maxScale === undefined ? 0 : window.maxScale * OGC_TO_SCREEN,
    // Esri: most zoomed in. 0 means no limit.
    maxScale: window.minScale === undefined ? 0 : window.minScale * OGC_TO_SCREEN,
  };
}

/** The OGC scale denominator of the XYZ tile grid at a zoom level. */
export function ogcScaleForZoom(zoom: number): number {
  return OGC_SCALE_AT_Z0 / 2 ** zoom;
}

/** The first integer zoom level whose tiles fall inside a scale window. */
export function firstVisibleZoom(window: ScaleWindow): number {
  if (window.maxScale === undefined) return 0;
  return Math.ceil(Math.log2(OGC_SCALE_AT_Z0 / window.maxScale));
}

/** The last integer zoom level whose tiles fall inside a scale window. */
export function lastVisibleZoom(window: ScaleWindow, maxZoom: number): number {
  if (window.minScale === undefined) return maxZoom;
  return Math.min(maxZoom, Math.floor(Math.log2(OGC_SCALE_AT_Z0 / window.minScale)));
}

/**
 * The nearest zoom to `zoom` at which a layer draws.
 *
 * Expressed in zoom levels rather than in a scale denominator because the view
 * snaps to whole levels: asked to go to the boundary scale itself, it rounds to
 * the nearest level, which is as likely to be the one outside the window as the
 * one inside. Clamping in the units the tile grid is actually quantized to
 * cannot land on the wrong side.
 */
export function clampZoomToWindow(window: ScaleWindow, zoom: number, maxZoom: number): number {
  const first = firstVisibleZoom(window);
  const last = lastVisibleZoom(window, maxZoom);
  if (zoom < first) return Math.min(first, maxZoom);
  if (zoom > last) return last;
  return zoom;
}

/** Where a zoom level sits relative to a layer's scale window. */
export type ScaleVisibility = 'visible' | 'zoom-in' | 'zoom-out';

/**
 * Whether a layer can draw at a zoom, and if not, which way to go.
 *
 * `'zoom-in'` means the view is too far out - the scale denominator is above
 * `maxScale` - so the user needs a larger zoom number.
 */
export function visibilityAtZoom(window: ScaleWindow, zoom: number): ScaleVisibility {
  const scale = ogcScaleForZoom(zoom);
  if (window.maxScale !== undefined && scale > window.maxScale) return 'zoom-in';
  if (window.minScale !== undefined && scale < window.minScale) return 'zoom-out';
  return 'visible';
}

/**
 * Normalize a scale denominator read from a capabilities document.
 *
 * GeoServer writes two values that mean "no limit" rather than omitting the
 * element: `0`, and `Double.MAX_VALUE` serialized as `1.7976931348623157E308`.
 * Both are in the live document. Stored as written, the first would hide every
 * layer that has it and the second is just noise.
 */
export function normalizeScaleDenominator(raw: unknown): number | undefined {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  if (value >= Number.MAX_VALUE) return undefined;
  return value;
}
